import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Check, House, WhatsappLogo, EnvelopeSimple, ArrowSquareOut, Users, Bed, Bathtub, ArrowsOut, CalendarCheck, Laptop } from "@phosphor-icons/react";
import { Navbar, scrollToId } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { BookingWidget } from "@/components/BookingWidget";
import { LongstayStrip } from "@/components/LongstayStrip";
import { Reveal } from "@/components/Reveal";
import { api, waLink, CONTACT_EMAIL } from "@/lib/api";
import { pick, lang2 } from "@/lib/localize";

export default function ApartmentDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = lang2(i18n.language);
  const [params] = useSearchParams();
  const [apt, setApt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const initialRange = useMemo(() => {
    const ci = params.get("checkin");
    const co = params.get("checkout");
    if (ci && co) {
      const from = new Date(`${ci}T00:00:00`);
      const to = new Date(`${co}T00:00:00`);
      if (!isNaN(from) && !isNaN(to) && to > from) return { from, to };
    }
    return undefined;
  }, [params]);
  const initialGuests = Math.max(1, parseInt(params.get("guests") || "2") || 2);

  useEffect(() => {
    api.get(`/apartments/${slug}`).then((r) => setApt(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  // Si viene con fechas elegidas desde el buscador, bajar directo a la reserva
  useEffect(() => {
    if (apt && initialRange) {
      const id = setTimeout(() => scrollToId("#reservar"), 700);
      return () => clearTimeout(id);
    }
  }, [apt, initialRange]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center gap-6">
        <p className="font-serif text-4xl text-ink">{t("detail.notFound")}</p>
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-terracotta">{t("detail.back")}</Link>
      </div>
    );
  }

  if (!apt) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <span className="text-xs uppercase tracking-[0.3em] text-ink/40">…</span>
      </div>
    );
  }

  const photos = apt.photos.slice(0, 9);

  const layout = (() => {
    const cls = [];
    let rest = photos.length;
    if (rest > 0) { cls.push("col-span-12 md:col-span-7 h-[300px] md:h-[460px]"); rest -= 1; }
    if (rest > 0) { cls.push("col-span-12 md:col-span-5 h-[300px] md:h-[460px]"); rest -= 1; }
    while (rest > 0) {
      if (rest === 1) {
        cls.push("col-span-12 h-[220px] md:h-[320px]");
        rest -= 1;
      } else if (rest === 2 || rest === 4) {
        cls.push("col-span-12 md:col-span-6 h-[220px] md:h-[300px]");
        cls.push("col-span-12 md:col-span-6 h-[220px] md:h-[300px]");
        rest -= 2;
      } else {
        cls.push("col-span-6 md:col-span-4 h-[180px] md:h-[260px]");
        cls.push("col-span-6 md:col-span-4 h-[180px] md:h-[260px]");
        cls.push("col-span-12 md:col-span-4 h-[220px] md:h-[260px]");
        rest -= 3;
      }
    }
    return cls;
  })();
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${apt.lat},${apt.lng}`;

  return (
    <div data-testid="apartment-detail" className="bg-sand min-h-screen">
      <div className="noise-overlay" />
      <Navbar />
      <div className="pt-20 md:pt-24">
        <LongstayStrip />
      </div>

      <div className="pt-8 md:pt-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        <Reveal>
          <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/50 hover:text-terracotta transition-colors">
            <ArrowLeft size={14} /> {t("detail.back")}
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.35em] text-terracotta">{apt.zone}</p>
          <h1 className="font-serif text-5xl md:text-7xl text-ink leading-[0.95] mt-2">{apt.name}</h1>
          <p className="font-serif italic text-xl md:text-2xl text-ink/55 mt-4 max-w-2xl">{pick(apt.tagline_i18n, lang)}</p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-xs uppercase tracking-[0.12em] text-ink/55">
            <span className="flex items-center gap-1.5"><Users size={15} /> {apt.guests} {t("detail.guests")}</span>
            <span className="flex items-center gap-1.5"><Bed size={15} /> {apt.bedrooms} {t("detail.bedrooms")}</span>
            <span className="flex items-center gap-1.5"><Bathtub size={15} /> {apt.bathrooms} {t("detail.baths")}</span>
            <span className="flex items-center gap-1.5"><ArrowsOut size={15} /> {apt.area} {t("detail.area")}</span>
          </div>

          <Link
            to="/estancias-largas"
            data-testid="detail-longstay-badge"
            className="mt-8 flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4.5 text-xs md:text-sm uppercase tracking-[0.12em] text-sand font-medium shadow-lg shadow-terracotta/25 hover:bg-ink transition-colors duration-300 w-full text-center leading-relaxed"
            style={{ paddingTop: "1.1rem", paddingBottom: "1.1rem" }}
          >
            <Laptop size={18} className="shrink-0" /> {t("detail.longstayBadge")}
          </Link>

          <div className="flex flex-wrap gap-3 mt-8">
            <button
              data-testid="detail-book-btn"
              onClick={() => scrollToId("#reservar")}
              className="flex items-center gap-2 px-7 py-3.5 bg-terracotta text-sand text-xs uppercase tracking-[0.2em] hover:bg-atlantic transition-colors"
            >
              <CalendarCheck size={15} /> {t("nav.book")}
            </button>
            <a
              data-testid="detail-whatsapp-btn"
              href={waLink(t("whatsapp.booking", { apartment: apt.name, checkin: "…", checkout: "…" }))}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-7 py-3.5 border border-ink/25 text-ink text-xs uppercase tracking-[0.2em] hover:border-[#25D366] hover:text-[#1da851] transition-colors"
            >
              <WhatsappLogo size={15} /> WhatsApp
            </a>
            <a
              data-testid="detail-contact-btn"
              href="/#contacto"
              className="flex items-center gap-2 px-7 py-3.5 border border-ink/25 text-ink text-xs uppercase tracking-[0.2em] hover:border-atlantic hover:text-atlantic transition-colors"
            >
              <EnvelopeSimple size={15} /> {t("nav.contact")}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="grid grid-cols-12 gap-3 mt-12" data-testid="photo-grid">
            {photos.map((p, i) => (
              <div key={i} className={`overflow-hidden border border-line ${layout[i] || "col-span-12"}`}>
                <img data-testid={`detail-photo-${i}`} src={p} alt={apt.name} loading={i < 2 ? "eager" : "lazy"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1400ms]" />
              </div>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-12 gap-10 md:gap-14 mt-14">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="border-t border-line pt-10">
                <h2 className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-5">{t("detail.house")}</h2>
                <p className="text-base md:text-lg font-light text-ink/75 leading-relaxed max-w-2xl">
                  {pick(apt.description_i18n, lang)}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-12 border-t border-line pt-10">
                <h2 className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-6">{t("detail.amenities")}</h2>
                <ul className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6">
                  {apt.amenities.map((key) => (
                    <li key={key} className="flex items-center gap-2.5 text-sm text-ink/70">
                      <Check size={14} weight="bold" className="text-terracotta shrink-0" />
                      {t(`amenities.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-12 border-t border-line pt-10">
                <h2 className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-4">{t("detail.where")}</h2>
                <p className="flex items-center gap-2 text-sm text-ink/70 mb-5">
                  <MapPin size={16} className="text-terracotta" /> {apt.zone}
                </p>
                <div className="relative border border-line bg-white p-2" data-testid="detail-map">
                  <div className="relative overflow-hidden">
                    {typeof localStorage !== "undefined" && localStorage.getItem("ldz-cookies") === "reject" ? (
                      <div data-testid="map-blocked" className="w-full h-[320px] md:h-[380px] bg-sand flex flex-col items-center justify-center gap-4 text-center px-6">
                        <MapPin size={28} className="text-terracotta" />
                        <p className="text-xs text-ink/60 max-w-sm leading-relaxed">{t("detail.mapBlocked")}</p>
                        <a
                          data-testid="open-gmaps-blocked"
                          href={gmapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white border border-line shadow px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ink flex items-center gap-2 hover:text-terracotta hover:border-terracotta transition-colors"
                        >
                          <ArrowSquareOut size={14} /> {t("detail.openMaps")}
                        </a>
                      </div>
                    ) : (
                      <>
                        <iframe
                          title={`Mapa ${apt.name}`}
                          src={`https://www.google.com/maps?q=${apt.lat},${apt.lng}&z=15&output=embed`}
                          className="w-full h-[320px] md:h-[380px] block"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="wa-pulse w-12 h-12 rounded-full bg-terracotta border-[3px] border-white shadow-xl flex items-center justify-center -translate-y-6" data-testid="map-house-icon">
                            <House size={22} weight="fill" className="text-white" />
                          </div>
                        </div>
                        <a
                          data-testid="open-gmaps"
                          href={gmapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute bottom-4 right-4 bg-white border border-line shadow-lg px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ink flex items-center gap-2 hover:text-terracotta hover:border-terracotta transition-colors"
                        >
                          <ArrowSquareOut size={14} /> {t("detail.openMaps")}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5" id="reservar">
            <div className="lg:sticky lg:top-24">
              <Reveal delay={0.1}>
                <BookingWidget apartment={apt} initialRange={initialRange} initialGuests={initialGuests} />
              </Reveal>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
